import "pure-react-carousel/dist/react-carousel.es.css";

import ArrowBack from "@mui/icons-material/ArrowBackIos";
import ArrowForward from "@mui/icons-material/ArrowForwardIos";
import { Box, Grid, Theme, Typography, styled } from "@mui/material";
import { BoxTypeMap, SxProps } from "@mui/system";
import { DateTime } from "luxon";
import { ButtonBack, ButtonNext, CarouselProvider, Slide, Slider } from "pure-react-carousel";
import React, { Dispatch, SetStateAction, useEffect } from "react";

import { BlockType, EnhancedBlock } from "../../Models/Block";
import { ServiceScheduleDay } from "../../Models/Calendar";
import { EnhancedOpsSlot } from "../../Models/OpsSlot";

interface Props {
  /** The currently selected day. */
  selectedDay?: DateTime;
  /** Flag indicating that the entire component should be disabled. */
  disabled?: boolean;
  /** The schedule days for the currently selected service. */
  serviceSchedule: ServiceScheduleDay<EnhancedBlock | EnhancedOpsSlot>[];
  /** Callback for when the user selects a new selected day. */
  setSelectedDay: Dispatch<SetStateAction<DateTime | undefined>>;
}

const BookingWizardDaySelector: React.FC<Props> = ({
  selectedDay,
  disabled = false,
  serviceSchedule,
  setSelectedDay
}: Props) => {
  // when the schedule changes, if day not already set select the first open day
  useEffect(() => {
    setSelectedDay(oldDay => {
      if (oldDay) return oldDay;
      const slotPadding = DateTime.now().plus({ minutes: Number(process.env.REACT_APP_TIMESLOT_PADDING_MINUTES || 5) });

      return serviceSchedule
        .find(({ dayState, blocks }) => dayState === BlockType.Open && blocks.some(b => b.startTime > slotPadding))
        ?.date.plus({ minutes: 0 });
    });
  }, [serviceSchedule]);

  const dayIndex = selectedDay
    ? serviceSchedule.findIndex(d => d.date.startOf("day").equals(selectedDay.startOf("day")))
    : undefined;
  const slotPadding = DateTime.now().plus({ minutes: Number(process.env.REACT_APP_TIMESLOT_PADDING_MINUTES || 5) });
  return (
    <CarouselProvider
      naturalSlideWidth={80}
      naturalSlideHeight={27}
      totalSlides={serviceSchedule.length}
      visibleSlides={8}
      currentSlide={dayIndex !== -1 ? dayIndex : undefined}
    >
      <Grid container alignItems="center">
        <Grid item>
          <ButtonBack disabled={disabled} style={{ backgroundColor: "unset", border: "unset", padding: "unset" }}>
            <ArrowBack fontSize="small" />
          </ButtonBack>
        </Grid>
        <Grid item xs>
          <Slider>
            <Grid container>
              {serviceSchedule.map(({ date, dayState, blocks }, index) => {
                // override day state as blocked if there are no available blocks that don't violate the padded time
                const state = !blocks.some(b => b.startTime > slotPadding) ? BlockType.Blocked : dayState;
                return (
                  <Slide key={index} index={index}>
                    <Box px={0.5}>
                      <DayItem
                        slotState={
                          selectedDay && date.startOf("day").equals(selectedDay.startOf("day")) ? "selected" : state
                        }
                        disabled={disabled}
                        onClick={() =>
                          !disabled &&
                          (state === BlockType.Open || state === BlockType.Reserved) &&
                          setSelectedDay(date)
                        }
                      >
                        <Typography variant="body2" align="center" component="div" sx={{ mb: -0.5 }}>
                          <strong>{date.toFormat("EEEE")}</strong>
                        </Typography>
                        <Typography variant="caption" align="center" component="div">
                          {date.toLocaleString(DateTime.DATE_MED)}
                        </Typography>
                      </DayItem>
                    </Box>
                  </Slide>
                );
              })}
            </Grid>
          </Slider>
        </Grid>
        <Grid item>
          <ButtonNext disabled={disabled} style={{ backgroundColor: "unset", border: "unset", padding: "unset" }}>
            <ArrowForward fontSize="small" />
          </ButtonNext>
        </Grid>
      </Grid>
    </CarouselProvider>
  );
};

interface DayItemProps extends Partial<BoxTypeMap> {
  disabled: boolean;
  slotState: BlockType | "selected";
}

const DayItem = styled(Box)<DayItemProps>(({ disabled, slotState, theme }) => {
  let style: SxProps<Theme>;
  switch (slotState) {
    case BlockType.Blocked:
      style = {
        backgroundColor: theme.palette.action.disabled,
        color: theme.palette.text.primary,
        cursor: "not-allowed"
      };
      break;
    case BlockType.Booked:
      style = {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.background.paper,
        cursor: "not-allowed"
      };
      break;
    case BlockType.Open:
      style = {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        cursor: "pointer"
      };
      break;
    case BlockType.Reserved:
      style = {
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.secondary.contrastText,
        cursor: "not-allowed"
      };
      break;
    case "selected":
      style = {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        cursor: "pointer"
      };
      break;
    default:
      throw `Failed to define the slot state style for ${slotState}.`;
  }
  if (disabled) style = { ...style, opacity: 0.7, cursor: "not-allowed" };
  return {
    "&": {
      ...style,
      borderRadius: "3px",
      border: `solid 1px ${theme.palette.text.disabled}`
    }
  };
});

export default BookingWizardDaySelector;
